
import React, { useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import {
  RichTextEditor,
  MenuButtonAlignCenter,
  MenuButtonAlignJustify,
  MenuButtonAlignLeft,
  MenuButtonAlignRight,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonHorizontalRule,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
} from "mui-tiptap";
import {useMediaQuery, useTheme} from "@mui/material" 
import {
  FaTrash,
} from "react-icons/fa";


import ApiCallService from "../../Service/ApiCallService";
import Spinner from "../common/Spinner";
import InputField from "../common/InputField";
import theme from "../../theme/themeConfig";
import { Button } from "@mui/material";

const MAX_WORDS = 100;
const tiptapExtensions = [
  StarterKit,
  Underline,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
];

const EditJourneyPage = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const [loading, setLoading] = useState(false);
  const [journey, setJourney] = useState([
    {
      position: "",
      startDate: "",
      endDate: "",
      isPresent: false,
      description: "",
      documents: [],
    },
  ]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const stripHtml = (html = "") => {
    if (typeof document !== "undefined") {
      const element = document.createElement("div");
      element.innerHTML = html;
      return (element.textContent || element.innerText || "")
        .replace(/\s+/g, " ")
        .trim();
    }

    const withSpaces = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    return withSpaces.replace(/\s+/g, " ").trim();
  };

  const countWords = (text = "") =>
    stripHtml(text).split(/\s+/).filter(Boolean).length;


  const handleChange = (idx, field, value) => {
    const updated = [...journey];

    if (field === "description") {
      updated[idx][field] = value;
    }


    else if (field === "endDate") {
      const startDate = updated[idx].startDate;

      if (value && startDate && !isSixMonthsGap(startDate, value)) {
        message.warning("End date must be at least 6 months after start date");
        return;
      }

      updated[idx][field] = value;
    }


    else if (field === "startDate") {
      const endDate = updated[idx].endDate;

      if (!updated[idx].isPresent && endDate && !isSixMonthsGap(value, endDate)) {
        message.warning("End date must be at least 6 months after start date");
        return;
      }

      updated[idx][field] = value;
    }

    else if (field === "isPresent") {
      updated[idx].isPresent = value;
      if (value) {
        updated[idx].endDate = "";
      }
    }

    else {
      updated[idx][field] = value;
    }

    setJourney(updated);
    return true;
  };

  const hasDescriptionWordLimitError = journey.some(
    (entry) => countWords(entry.description) > MAX_WORDS,
  );

  const isSixMonthsGap = (start, end) => {
    if (!start || !end) return true;

    const startDate = new Date(start);
    const endDate = new Date(end);

    const minEndDate = new Date(startDate);
    minEndDate.setMonth(minEndDate.getMonth() + 6);

    return endDate >= minEndDate;
  };

  const handleAdd = () => {
    setJourney([
      ...journey,
      {
        position: "",
        startDate: "",
        endDate: "",
        isPresent: false,
        description: "",
        documents: [],
      },
    ]);
  };

  const handleRemove = (idx) => {
    setJourney(journey.filter((_, i) => i !== idx));
  };

  const detectType = (file) => {
    if (file.type.startsWith("image")) return "image";
    if (file.type.startsWith("video")) return "video";
    return "document";
  };

  const handleFileUpload = (idx, file) => {
    if (!file) return;

    const localFile = {
      file,
      preview: URL.createObjectURL(file),
      type: detectType(file),
      name: file.name,
    };

    const updated = [...journey];
    updated[idx].documents.push(localFile);
    setJourney(updated);
  };

  const renderPreview = (file) => {
    if (file.type === "image") {
      return (
        <img
          src={file.preview}
          alt={file.name}
          className="w-full h-28 object-cover rounded"
        />
      );
    }


    if (file.type === "document") {
      return (
        <iframe
          src={file.preview}
          className="w-full h-full rounded"
          title="pdf"
        />
      );
    }


    if (file.type === "video") {
      return (
        <video src={file.preview} controls className="w-full h-28 rounded" />
      );
    }

    return (
      <div className="text-sm text-blue-700 underline truncate">
        {file.name}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      for (const entry of journey) {
        const descriptionWords = countWords(entry.description);

        if (descriptionWords === 0) {
          message.error("Please enter a journey description");
          setLoading(false);
          return;
        }

        if (descriptionWords > MAX_WORDS) {
          message.error("Description cannot exceed 100 words");
          setLoading(false);
          return;
        }

        if (!entry.isPresent && !entry.endDate) {
          message.error("Please select an end date or mark the role as present");
          setLoading(false);
          return;
        }

        if (
          entry.endDate &&
          new Date(entry.endDate) < new Date(entry.startDate)
        ) {
          message.error("Please fix invalid date ranges before saving");
          setLoading(false);
          return;
        }
      }

      const payload = [];

      for (const entry of journey) {
        const uploadedDocs = [];

        for (const doc of entry.documents) {
          const fd = new FormData();
          fd.append("file", doc.file);

          const res = await ApiCallService.post(
            `${process.env.REACT_APP_BASE_URL}/upload/program/document`,
            fd,
          );

          uploadedDocs.push(res.s3Key);
        }

        const { isPresent, ...journeyEntry } = entry;

        payload.push({
          ...journeyEntry,
          endDate: isPresent ? null : journeyEntry.endDate,
          documents: uploadedDocs,
        });
      }

      let res = await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/${userId}/journey`,
        payload,
      );

      message.success(res.message);
      navigate(-1);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save journey");
      console.log(err)
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-10">
      <Spinner loading={loading} />

      <div className="max-w-5xl mx-auto px-4 mb-4 flex justify-between">
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{display:"flex", flexDirection:"row", gap:1}}
        >
          <ArrowBackIcon />
          {!isMobile && "Back"}
        </Button>
        <h2 className="flex-1 text-center font-semibold">Create Journey</h2>
      </div>

      <div className="max-w-5xl 2xl:max-w-7xl mx-auto bg-white rounded-lg p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {journey.map((entry, idx) => (
            <div key={idx} className="border rounded-lg">
              <div className="flex justify-between items-start p-4 border-b">
                <div>
                  <h3 className="font-semibold">
                    <b>New Role</b>
                  </h3>
                  <p className="text-sm text-gray-500">
                    Add details about your professional journey.
                  </p>
                </div>

                {journey.length > 1 && (
                  <FaTrash
                    onClick={() => handleRemove(idx)}
                    className="cursor-pointer text-gray-400 hover:text-red-500"
                  />
                )}
              </div>

              <div className="p-4 space-y-6">
                {/* Position, Start Date, End Date */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    type="text"
                    label="Position / Role"
                    value={entry.position}
                    placeholder="Enter position or role"
                    onChange={(v) => handleChange(idx, "position", v)}
                    required
                  />

                  <InputField
                    type="date"
                    label="Start Date"
                    value={entry.startDate}
                    onChange={(v) => handleChange(idx, "startDate", v)}
                    required
                  />

                  <div>
                    <InputField
                      type="date"
                      label="End Date"
                      value={entry.endDate}
                      onChange={(v) => handleChange(idx, "endDate", v)}
                      disabled={entry.isPresent}
                      required={!entry.isPresent}
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={entry.isPresent}
                        onChange={(e) =>
                          handleChange(idx, "isPresent", e.target.checked)
                        }
                      />
                      Present
                    </label>
                  </div>
                </div>

                {/* Description Full Width */}
                <div>
                  <label
                    className={`block mb-2 font-medium ${
                      countWords(entry.description) > MAX_WORDS ? "text-red-600" : ""
                    }`}
                  >
                    Description ({countWords(entry.description)}/{MAX_WORDS} words)
                  </label>
                  <RichTextEditor
                    extensions={tiptapExtensions}
                    content={entry.description}
                    onUpdate={({ editor }) => {
                      handleChange(idx, "description", editor.getHTML());
                    }}
                    renderControls={() => (
                      <MenuControlsContainer>
                        <MenuSelectHeading />
                        <MenuDivider />
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonUnderline />
                        <MenuButtonStrikethrough />
                        <MenuDivider />
                        <MenuButtonBulletedList />
                        <MenuButtonOrderedList />
                        <MenuDivider />
                        <MenuButtonAlignLeft />
                        <MenuButtonAlignCenter />
                        <MenuButtonAlignRight />
                        <MenuButtonAlignJustify />
                        <MenuDivider />
                        <MenuButtonBlockquote />
                        <MenuButtonCode />
                        <MenuButtonHorizontalRule />
                        <MenuDivider />
                        <MenuButtonRemoveFormatting />
                        <MenuDivider />
                        <MenuButtonUndo />
                        <MenuButtonRedo />
                      </MenuControlsContainer>
                    )}
                    editorProps={{
                      attributes: {
                        class: "min-h-[140px] px-3 py-2 outline-none",
                      },
                    }}
                  />
                </div>

                <hr />

                {/* Documents */}
                <div>
                  <label className="flex items-center gap-2 font-medium mb-3">
                    <b>Documents / Media</b>
                  </label>

                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <label className="w-40 h-28 border-2 border-dashed rounded-lg flex flex-col justify-center items-center cursor-pointer text-sm text-gray-500 hover:border-purple-400">
                      <input
                        type="file"
                        hidden
                        onChange={(e) =>
                          handleFileUpload(idx, e.target.files[0])
                        }
                      />
                      ⬆ <span>Click to upload</span>
                      <span className="text-xs">SVG, PNG, JPG, GIF</span>
                    </label>

                    {entry.documents.map((file, i) => (
                      <div
                        key={i}
                        className="w-24 h-24 sm:w-32 sm:h-28 2xl:w-40 2xl:h-36 border rounded flex flex-col justify-center items-center text-xs relative"
                      >
                        {renderPreview(file)}
                        <FaTrash
                          className="absolute top-1 right-1 text-red-400 cursor-pointer"
                          onClick={() => {
                            const updated = [...journey];
                            updated[idx].documents.splice(i, 1);
                            setJourney(updated);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <button
              type="submit"
              disabled={hasDescriptionWordLimitError || loading}
              className="px-5 py-2 text-sm rounded text-white"
              style={{
                backgroundColor: theme.palette.primary.main,
                cursor: hasDescriptionWordLimitError || loading ? "not-allowed" : "pointer",
                opacity: hasDescriptionWordLimitError || loading ? 0.55 : 1,
              }}
            >
              Save Journey
            </button>

            <button
              type="button"
              onClick={handleAdd}
              className="px-2 py-2 text-sm rounded text-white"
              style={{ backgroundColor: theme.palette.primary.main }}
            >
              + Add Another Journey
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditJourneyPage;
